/**
 * Service de génération PDF avec modern-screenshot et jsPDF
 * Capture l'écran et génère un PDF identique (supporte oklch)
 */

import { domToPng } from 'modern-screenshot';
import { jsPDF } from 'jspdf';

export async function generatePDF(
  elementId: string,
  filename: string,
  options?: {
    format?: 'a4' | 'letter' | 'a3';
    orientation?: 'portrait' | 'landscape';
    scale?: number;
    fit?: 'width' | 'contain';
  }
): Promise<void> {
  let loadingDiv: HTMLElement | null = null;
  
  try {
    // Afficher un indicateur de chargement
    loadingDiv = document.createElement('div');
    loadingDiv.id = 'pdf-loading';
    loadingDiv.className = 'fixed inset-0 bg-black/50 z-[100] flex items-center justify-center';
    loadingDiv.innerHTML = `
      <div class="bg-white rounded-lg p-8 shadow-2xl">
        <div class="flex flex-col items-center gap-4">
          <div class="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-lg font-semibold text-gray-900">Génération du PDF...</p>
          <p class="text-sm text-gray-600">Cela peut prendre quelques secondes</p>
        </div>
      </div>
    `;
    document.body.appendChild(loadingDiv);

    // Attendre un peu pour que l'animation s'affiche
    await new Promise(resolve => setTimeout(resolve, 100));

    // Trouver l'élément
    const element = document.getElementById(elementId);
    
    if (!element) {
      throw new Error(`Élément avec l'ID "${elementId}" introuvable`);
    }

    console.log('Element trouvé:', element);
    console.log('Dimensions:', {
      width: element.offsetWidth,
      height: element.offsetHeight,
      scrollWidth: element.scrollWidth,
      scrollHeight: element.scrollHeight
    });

    // Ajouter temporairement des styles pour cacher les boutons PDF et optimiser pour PDF
    const style = document.createElement('style');
    style.id = 'pdf-hide-buttons';
    style.innerHTML = `
      /* Cacher les boutons "Télécharger PDF" pendant la génération */
      #${elementId} button {
        display: none !important;
      }
      /*
        Capture (modern-screenshot → foreignObject → canvas) :
        - embedWebFont() sous-ensemble les glyphes par caractère ; mélange WOFF2 / système
          → traits verticaux (l, I, i) souvent plus « gras » que le reste → font: false
          dans domToPng (voir plus bas).
        - Police système homogène + pas de graisse synthétique + lissage gris (Windows).
      */
      #${elementId}, #${elementId} * {
        font-synthesis: none !important;
        font-variation-settings: normal !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
        text-rendering: optimizeSpeed !important;
      }
      #${elementId}, #${elementId} * {
        font-family: Arial, Helvetica, "Helvetica Neue", "Liberation Sans", sans-serif !important;
      }
      /* Empêcher les césures agressives dans les tableaux (évite "T" puis "I" sur 2 lignes) */
      #${elementId} table th,
      #${elementId} table td {
        word-break: normal !important;
        overflow-wrap: normal !important;
        hyphens: none !important;
      }
      /* Retirer les limites de hauteur et overflow pour capturer tout le contenu */
      #${elementId} .bz-telecom-content-scroll,
      #${elementId} .prisme-content-scroll {
        max-height: none !important;
        overflow: visible !important;
      }
      /* Optimisations spécifiques pour l'Offre de service BZ */
      #${elementId} {
        background-color: #ffffff !important;
        padding: 0 !important;
        max-height: none !important;
        overflow: visible !important;
      }
      #${elementId} > div {
        page-break-inside: avoid;
      }
      /* Améliorer les espacements pour PDF */
      #${elementId} .space-y-8 > * {
        margin-bottom: 1.5rem !important;
        page-break-inside: avoid;
      }
      /* Éviter les coupures dans les cartes importantes */
      #${elementId} .rounded-2xl,
      #${elementId} .rounded-xl {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      /* Améliorer les tableaux pour PDF */
      #${elementId} table {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      /* Assurer que les graphiques ne sont pas coupés */
      #${elementId} canvas,
      #${elementId} svg {
        page-break-inside: avoid;
      }
    `;
    document.head.appendChild(style);

    // Attendre que les styles soient appliqués et que le DOM se réajuste
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      await document.fonts?.ready;
    } catch {
      /* ignore */
    }

    // Capturer l'élément avec modern-screenshot (supporte oklch nativement)
    const scale =
      options?.scale ??
      (element.scrollHeight > 10000 ? 1.75 : element.scrollHeight > 6000 ? 2.25 : 2.75);
    const captureW = Math.max(2, Math.ceil(element.scrollWidth / 2) * 2);
    const captureH = Math.max(2, Math.ceil(element.scrollHeight / 2) * 2);
    const dataUrl = await domToPng(element, {
      scale,
      width: captureW,
      height: captureH,
      backgroundColor: '#ffffff', // Fond blanc pour PDF
      quality: 1.0,
      /* Évite embedWebFont : sous-ensembles par glyphe → poids visuel incohérent (l, I, i) */
      font: false,
    });

    // Retirer le style temporaire
    const tempStyle = document.getElementById('pdf-hide-buttons');
    if (tempStyle) {
      document.head.removeChild(tempStyle);
    }

    // Convertir dataUrl en image pour obtenir les dimensions
    const img = new Image();
    img.src = dataUrl;
    await new Promise((resolve) => {
      img.onload = resolve;
    });

    console.log('Image capturée:', {
      width: img.width,
      height: img.height
    });

    // Créer le PDF
    const pdf = new jsPDF({
      orientation: options?.orientation ?? 'portrait',
      unit: 'mm',
      format: options?.format || 'a4',
      compress: true
    });

    // Dimensions dynamiques selon le format (A4 vs Letter)
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Marges optimisées (mm)
    const leftMargin = 5;
    const rightMargin = 5;
    const topMargin = 10;
    const bottomMargin = 10;

    const pageContentWidth = pageWidth - leftMargin - rightMargin;
    const pageContentHeight = pageHeight - topMargin - bottomMargin;

    // Dimensions rendues dans la page
    // - fit=width: utilise toute la largeur utile, hauteur proportionnelle (peut déborder -> multi-pages)
    // - fit=contain: tient sur 1 page (réduit légèrement si nécessaire)
    const fitMode = options?.fit || 'width';
    let renderWidth = pageContentWidth;
    let renderHeight = (img.height * renderWidth) / img.width;
    if (fitMode === 'contain' && renderHeight > pageContentHeight) {
      renderHeight = pageContentHeight;
      renderWidth = (img.width * renderHeight) / img.height;
    }

    console.log('Dimensions PDF:', {
      format: options?.format || 'a4',
      pageWidth,
      pageHeight,
      renderWidth,
      renderHeight,
      pageContentHeight,
      fitMode,
    });
    
    // Si l'image tient sur une seule page
    if (fitMode === 'contain' || renderHeight <= pageContentHeight) {
      // Centrer horizontalement si on a dû réduire la largeur (fit=contain)
      const x = fitMode === 'contain' ? leftMargin + (pageContentWidth - renderWidth) / 2 : leftMargin;
      pdf.addImage(dataUrl, 'PNG', x, topMargin, renderWidth, renderHeight, undefined, 'SLOW');
      console.log('PDF créé avec 1 page');
    } else {
      // Multi-pages : découper intelligemment avec chevauchement minimal
      let currentY = 0;
      let pageNumber = 1;
      const overlap = 5; // 5mm de chevauchement pour éviter les coupures nettes
      
      // Première page
      pdf.addImage(
        dataUrl, 
        'PNG', 
        leftMargin, 
        topMargin, 
        pageContentWidth,
        renderHeight,
        undefined,
        'SLOW'
      );
      
      currentY = pageContentHeight - overlap;
      pageNumber = 1;
      
      // Pages suivantes
      while (currentY < renderHeight) {
        pdf.addPage();
        pageNumber++;
        
        // Position de l'image : on décale vers le haut pour montrer la suite
        const yPosition = topMargin - currentY;
        
        pdf.addImage(
          dataUrl, 
          'PNG', 
          leftMargin, 
          yPosition, 
          pageContentWidth,
          renderHeight,
          undefined,
          'SLOW'
        );
        
        currentY += pageContentHeight - overlap;
      }
      
      console.log(`PDF créé avec ${pageNumber} page(s)`);
    }

    console.log('PDF prêt à être sauvegardé');

    // Sauvegarder le PDF
    pdf.save(filename);

    console.log('PDF sauvegardé:', filename);

    // Retirer l'indicateur de chargement
    if (loadingDiv && loadingDiv.parentNode) {
      document.body.removeChild(loadingDiv);
    }

  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    
    // Retirer l'indicateur en cas d'erreur
    if (loadingDiv && loadingDiv.parentNode) {
      document.body.removeChild(loadingDiv);
    }
    
    // Message d'erreur plus informatif
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    alert(`Une erreur est survenue lors de la génération du PDF:\n${errorMessage}\n\nVeuillez réessayer.`);
  }
}

/**
 * Version simple de génération PDF (comme ChateauLaurier)
 * Utilise window.print() avec une fenêtre d'impression native du navigateur
 * - Clone le contenu HTML avec tous les styles CSS
 * - Convertit les canvas en images
 * - Format Letter (8.5x11) avec marges 0.75in
 * - Ouvre la boîte de dialogue d'impression du navigateur
 */
export async function generatePDFSimple(
  elementId: string,
  filename: string,
  options?: {
    headerFooter?: { logoUrl: string; clientName: string; documentTitle?: string };
    orientation?: 'portrait' | 'landscape';
    format?: 'letter' | 'a4' | 'a3';
  }
): Promise<void> {
  try {
    // Attendre un peu pour que le contenu soit rendu
    await new Promise(resolve => setTimeout(resolve, 500));

    // Trouver l'élément
    const contentElement = document.getElementById(elementId);
    if (!contentElement) {
      throw new Error(`Élément avec l'ID "${elementId}" introuvable`);
    }

    // Collecter tous les styles CSS de la page
    const collectPrintStyles = () => {
      const allStyles: string[] = [];

      Array.from(window.document.styleSheets).forEach((styleSheet) => {
        try {
          if (styleSheet.cssRules) {
            Array.from(styleSheet.cssRules).forEach((rule) => {
              if (rule instanceof CSSStyleRule || rule instanceof CSSMediaRule) {
                allStyles.push(rule.cssText);
              }
            });
          }
        } catch {
          // Ignore cross-origin stylesheets
        }
      });

      const inlineStyles = Array.from(window.document.querySelectorAll('style'))
        .map((style) => style.innerHTML)
        .join('\n');

      const stylesheetLinks = Array.from(
        window.document.querySelectorAll('link[rel="stylesheet"]')
      ).map((link) => (link as HTMLLinkElement).outerHTML);

      return {
        cssText: `${allStyles.join('\n')}\n${inlineStyles}`,
        linksHtml: stylesheetLinks.join('\n')
      };
    };

    // Cloner le contenu et convertir les canvas en images
    const clonePrintableContent = (element: HTMLElement) => {
      const cloned = element.cloneNode(true) as HTMLElement;
      const sourceCanvases = Array.from(element.querySelectorAll('canvas'));
      const clonedCanvases = Array.from(cloned.querySelectorAll('canvas'));

      sourceCanvases.forEach((canvas, index) => {
        const clonedCanvas = clonedCanvases[index];
        if (!clonedCanvas) return;
        try {
          const img = document.createElement('img');
          img.src = canvas.toDataURL('image/png');
          img.width = canvas.width;
          img.height = canvas.height;
          img.className = clonedCanvas.className;
          img.setAttribute('style', clonedCanvas.getAttribute('style') || '');
          clonedCanvas.replaceWith(img);
        } catch {
          // If canvas export fails, keep original canvas tag.
        }
      });

      return cloned;
    };

    // Attendre le chargement des images et polices
    const waitForPrintResources = async (doc: Document) => {
      const images = Array.from(doc.images || []);
      if (images.length) {
        await Promise.race([
          Promise.all(
            images.map(
              (img) =>
                new Promise<void>((resolve) => {
                  if (img.complete) {
                    resolve();
                    return;
                  }
                  img.addEventListener('load', () => resolve(), { once: true });
                  img.addEventListener('error', () => resolve(), { once: true });
                })
            )
          ),
          new Promise<void>((resolve) => setTimeout(resolve, 1500))
        ]);
      }

      if (doc.fonts?.ready) {
        try {
          await doc.fonts.ready;
        } catch {
          // ignore font readiness errors
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    };

    // Générer le HTML pour l'impression (option : bandeau bleu en haut avec titre, client, logo BZ)
    const getPrintHtml = (
      contentHtml: string,
      documentTitle: string,
      companyName: string,
      headerFooter?: { logoUrl: string; clientName: string; documentTitle?: string },
      page: { format: 'letter' | 'a4' | 'a3'; orientation: 'portrait' | 'landscape' } = { format: 'letter', orientation: 'portrait' }
    ) => {
      const { cssText, linksHtml } = collectPrintStyles();
      const clientName = headerFooter?.clientName ?? companyName;
      const bannerTitle = headerFooter?.documentTitle ?? documentTitle;
      const logoPath = headerFooter?.logoUrl || '/BZ.png';
      const logoSrc = logoPath.startsWith('http') ? logoPath : `${window.location.origin}${logoPath.startsWith('/') ? logoPath : '/' + logoPath}`;
      const bannerHtml = headerFooter
        ? `<div class="prisme-pdf-banner">
  <div class="prisme-pdf-banner-left">
    <div class="prisme-pdf-banner-title">${escapeHtml(bannerTitle)}</div>
    <div class="prisme-pdf-banner-client">${escapeHtml(clientName)}</div>
  </div>
  <div class="prisme-pdf-banner-logo"><img src="${logoSrc}" alt="BZ" /></div>
</div>`
        : '';
      const bannerCss = headerFooter
        ? `
    .prisme-pdf-banner { background: #406fb4; color: #fff; padding: 12px 24px; display: flex; align-items: center; justify-content: space-between; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; break-inside: avoid; page-break-inside: avoid; break-after: avoid-page; page-break-after: avoid; margin: -24px -24px 24px -24px; }
    .prisme-pdf-banner + * { break-before: avoid-page; page-break-before: avoid; }
    .prisme-pdf-banner-left { display: flex; flex-direction: column; gap: 2px; }
    .prisme-pdf-banner-title { font-size: 1.125rem; font-weight: 700; }
    .prisme-pdf-banner-client { font-size: 0.875rem; opacity: 0.95; }
    .prisme-pdf-banner-logo img { height: 32px; width: auto; display: block; object-fit: contain; }
    @media print {
      .prisme-pdf-banner { margin: 0 0 1rem 0; }
      .prisme-pdf-banner + * { break-before: avoid-page; page-break-before: avoid; }
    }`
        : '';
      return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(documentTitle)} – ${escapeHtml(companyName)}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <base href="${window.location.origin}/" />
  ${linksHtml}
  <style>
    ${cssText}
    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body { 
      font-family: Arial, Helvetica, "Helvetica Neue", "Liberation Sans", sans-serif;
      background: #ffffff; 
      margin: 0; 
      padding: 24px; 
      color: #1f2937;
      font-synthesis: none;
      font-variation-settings: normal;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeSpeed;
    }
    body * {
      font-family: inherit !important;
    }
    ${bannerCss}
    .prisme-page-break {
      height: 0;
      margin: 0;
    }
    table, thead, tbody, tr, td, th,
    img, svg, canvas, figure,
    .recharts-wrapper, .recharts-responsive-container,
    .shadow-lg, .shadow-xl {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .prisme-avoid-break {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    h1, h2, h3 {
      break-after: avoid;
      page-break-after: avoid;
    }
    @media print {
      body { padding: 0; }
      @page { size: ${page.format} ${page.orientation}; margin: 0.75in; }
      table th, table td {
        word-break: normal !important;
        overflow-wrap: normal !important;
        hyphens: none !important;
      }
      .prisme-page-break {
        break-before: page;
        page-break-before: always;
      }
      .prisme-avoid-break, table, thead, tbody, tr, img, svg, figure {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      h1, h2, h3 {
        break-after: avoid;
        page-break-after: avoid;
      }
    }
  </style>
</head>
<body>
  ${bannerHtml}
  ${contentHtml}
</body>
</html>`;
    };

    function escapeHtml(s: string): string {
      const el = document.createElement('div');
      el.textContent = s;
      return el.innerHTML;
    }

    // Cloner le contenu
    const printable = clonePrintableContent(contentElement);
    
    // Extraire le titre et le nom de la compagnie depuis l'élément ou utiliser des valeurs par défaut
    const titleElement = contentElement.querySelector('h1');
    const documentTitle = titleElement?.textContent || filename.replace('.pdf', '');
    const companyElement = contentElement.querySelector('p');
    const companyName = companyElement?.textContent || 'Client';
    const html = getPrintHtml(
      printable.outerHTML,
      documentTitle,
      companyName,
      options?.headerFooter,
      { format: options?.format ?? 'letter', orientation: options?.orientation ?? 'portrait' }
    );

    // Ouvrir une nouvelle fenêtre et écrire le HTML
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez que les popups ne sont pas bloquées.');
    }

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();    // Attendre que les ressources soient chargées puis ouvrir la boîte de dialogue d'impression
    printWindow.addEventListener('load', async () => {
      await waitForPrintResources(printWindow.document);
      printWindow.print();
    });

  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    alert(`Une erreur est survenue lors de la génération du PDF:\n${errorMessage}\n\nVeuillez réessayer.`);
  }
}
